import { TranslateService } from '@ngx-translate/core';
import { ChartDataset } from '@components/svg-chart';
import {YEAR_CHIP_COLORS} from '@components/date-picker';


/**
 * Data for a single year
 */
export interface YearData {
  year: number;
  monthlyData: Map<number, any>;
}

/**
 * Manager for multi-year weather data comparison
 * Handles creating datasets with different colors for each year
 */
export class MultiYearDataManager {
  constructor(private translateService: TranslateService) {}

  /**
   * Get color for a specific year index
   */
  getYearColor(index: number): string {
    return YEAR_CHIP_COLORS[index % YEAR_CHIP_COLORS.length];
  }

  /**
   * Create a dataset for a specific year
   * @param values Data values for the year
   * @param year The year number
   * @param yearIndex Index of the year in the selection (for color assignment)
   * @param indicatorLabel Translated indicator label
   * @param baseColor Optional base color (if not provided, uses palette)
   */
  createYearDataset(
    values: (number | null)[],
    year: number,
    yearIndex: number,
    indicatorLabel: string,
    baseColor?: string
  ): ChartDataset {
    const color = baseColor || this.getYearColor(yearIndex);

    return {
      label: `${indicatorLabel} (${year})`,
      data: values,
      borderColor: color,
      backgroundColor: color,
      borderWidth: 2
    };
  }

  /**
   * Process raw API data into monthly values
   * @param data Raw API response data
   * @param indicator Selected indicator (temperature, precipitation, etc.)
   * @param getIndicatorValue Function to extract indicator value from data item
   */
  processMonthlyData(
    data: any[],
    maxMonth: number,
    getIndicatorValue: (item: any, indicator: string) => number,
    indicator: string
  ): (number | null)[] {
    if (!data || data.length === 0) {
      return Array(maxMonth).fill(null);
    }

    // Create a map of data by month
    const dataByMonth = new Map<number, any>();
    data.forEach((item: any) => {
      if (item.month) {
        dataByMonth.set(item.month, item);
      }
    });

    // Generate values for each month
    const values: (number | null)[] = [];
    for (let month = 1; month <= maxMonth; month++) {
      const monthData = dataByMonth.get(month);
      if (monthData) {
        values.push(getIndicatorValue(monthData, indicator));
      } else {
        values.push(null);
      }
    }

    return values;
  }

  /**
   * Generate month labels (same for all years when comparing)
   * Always returns 12 months for consistent x-axis
   */
  generateMonthLabels(monthNames: string[]): string[] {
    return monthNames.slice(0, 12);
  }

  /**
   * Determine max month to show based on years
   * If current year is included, limit to current month
   * Otherwise show all 12 months
   */
  getMaxMonth(years: number[]): number {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    // If current year is selected, use current month as max
    if (years.includes(currentYear)) {
      return currentMonth;
    }

    // Otherwise show all 12 months
    return 12;
  }

  /**
   * Check if multiple years are selected
   */
  isMultiYearMode(years: number[]): boolean {
    return years.length > 1;
  }

  /**
   * Sort years descending (most recent first)
   */
  sortYearsDescending(years: number[]): number[] {
    return [...years].sort((a, b) => b - a);
  }
}
